/**
 * PKCE (RFC 7636) — shared by every OAuth flow in the SPA.
 *
 * ── Why this is not private to the Google provider any more ──────────────
 * It was, and the device-pairing page therefore did OAuth WITHOUT it: no
 * `code_challenge` on the way out, no `code_verifier` on the way back. The API
 * refuses such an exchange — correctly, failing closed — so pairing died with
 *
 *   [auth.google] exchange refused: no PKCE code_verifier
 *
 * The pairing page was not weakening a check; it never knew there was one to
 * satisfy, because the only implementation lived inside another module's
 * private scope. Shared code is the fix: a second flow that has to reimplement
 * a security control will get it wrong or skip it.
 *
 * ── Why the verifier goes in sessionStorage ──────────────────────────────
 * The flow leaves the page entirely and comes back through a redirect, so the
 * value has to outlive the document. sessionStorage is scoped to this tab and
 * this origin, and is cleared when the tab closes — the narrowest store that
 * survives a redirect. It is deleted the moment it is used, so a verifier is
 * never available to a later page load.
 */

function base64UrlEncode(bytes: Uint8Array): string {
  // Every element is 0–255, so each maps to exactly one code unit and
  // fromCodePoint is equivalent to fromCharCode here — btoa requires this
  // latin1 representation.
  let binary = ''
  for (const b of bytes) binary += String.fromCodePoint(b)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export function randomUrlSafe(bytes: number): string {
  const arr = new Uint8Array(bytes)
  crypto.getRandomValues(arr)
  return base64UrlEncode(arr)
}

/** S256 challenge. Google supports it, and `plain` defeats the point of PKCE. */
export async function deriveChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))
  return base64UrlEncode(new Uint8Array(digest))
}

/**
 * A verifier and its challenge.
 *
 * 32 bytes → 43 characters, the RFC 7636 minimum verifier length.
 */
export async function createPkcePair(): Promise<{ verifier: string; challenge: string }> {
  const verifier = randomUrlSafe(32)
  return { verifier, challenge: await deriveChallenge(verifier) }
}

/**
 * Where each flow parks its verifier across the redirect.
 *
 * Distinct keys, deliberately: a sign-in and a device approval can be started
 * from the same tab, and one overwriting the other would produce a mismatched
 * verifier and an exchange failure that looks like nothing in particular.
 */
export const PKCE_KEYS = {
  login: 'grizzly_auth_pkce_verifier',
  pair: 'grizzly_pair_pkce_verifier',
} as const

/**
 * Where the pairing flow parks its CSRF nonce.
 *
 * Sign-in has always had one (`state` compared against a stored value, failing
 * closed). Pairing did not: its state was `pair:<userCode>` with no random
 * component, and the callback accepted anything starting with `pair:`. What
 * actually blocked a forged callback was the verifier lookup throwing when
 * nothing was stashed — a real barrier, but an accidental one, and it did not
 * bind the returning state to the code this browser had submitted.
 */
export const PAIR_STATE_KEY = 'grizzly_pair_state'

export function stashVerifier(key: string, verifier: string): void {
  sessionStorage.setItem(key, verifier)
}

/** Read the verifier and immediately forget it: it is single-use by design. */
export function takeVerifier(key: string): string | null {
  const value = sessionStorage.getItem(key)
  sessionStorage.removeItem(key)
  return value
}
